import {Avatar, classNames, layout, MaterialIcon, PushButton, typography, usePlatform, View} from "@variocube/cube-ui";
import {CustomerDisplay} from "./customer";
import * as React from "react";
import {useCallback} from "react";
import {CubeOrder, Order} from "../types";
import {Occupancy} from "@variocube/app-api";
import {stylesheet} from "typestyle";
import {OrderDisplay, OrderStatus} from "./order";
import {findBoxByNumber} from "@variocube/app-common";
import {logger} from "../logger";
import {useLocalization} from "../../i18n";

const log = logger.sub("OrderDetail");

interface OrderDetailProps {
    detail: CubeOrder;
    onClose: () => void;
    onPickup: (occupancy: Occupancy) => void;
    onDelivery: (order: Order) => void;
}

export function OrderDetail({detail, onClose, onPickup, onDelivery}: OrderDetailProps) {
    const {order, occupancy} = detail;
    const {t} = useLocalization();
    const platform = usePlatform();

    const handlePickup = useCallback(() => {
        log.info("Starting pickup", occupancy);
        occupancy && onPickup(occupancy)
    }, [onPickup, occupancy]);

    const handleDeliver = useCallback(() => {
        log.info("Starting delivery", order);
        onDelivery(order)
    }, [onDelivery, order]);

    const handleOpenBox = useCallback(async () => {
        log.info("Open box", occupancy);
        if (occupancy) {
            try {
                const box = await findBoxByNumber(platform, occupancy?.boxNumber);
                if (box) {
                    await platform.openLock(box.lock);
                }
            }
            catch (error) {
                console.error("Error opening box", error);
            }
        }
    }, [platform, occupancy]);

    return (
        <View>
            <div className={classNames(layout.paddingLarge, layout.horizontal, layout.spaceBetween)}>
                <div>
                    <PushButton onClick={onClose} icon={MaterialIcon.arrow_back}>
                        {t("Back")}
                    </PushButton>
                </div>
                {occupancy && (
                    <div>
                        <PushButton primary onClick={handlePickup}>
                            {t("Pickup")}
                        </PushButton>
                    </div>
                )}
                {!occupancy && (
                    <div>
                        <PushButton primary onClick={handleDeliver}>
                            {t("Deliver")}
                        </PushButton>
                    </div>
                )}
            </div>
            <div className={classNames(layout.fill, layout.horizontal, layout.spaceEvenly)}>
                <div>
                    <div className={classNames(layout.paddingLarge, styles.bigger)}>
                        <div className={typography.muted}>{t("Order")}</div>
                        <OrderDisplay order={order}/>
                        <div>{order.internalNotes}</div>
                    </div>
                    <div className={classNames(layout.paddingLarge, styles.bigger)}>
                        <div className={typography.muted}>{t("Customer")}</div>
                        <CustomerDisplay customer={order.customer}/>
                    </div>
                </div>
                <div>
                    <div className={classNames(layout.paddingLarge, styles.bigger)}>
                        <div className={typography.muted}>{t("Status")}</div>
                        <OrderStatus status={order.orderState} />
                    </div>

                    {occupancy && (
                        <div className={classNames(layout.paddingLarge)}>
                            <div className={classNames(typography.muted, styles.bigger)}>{t("Box")}</div>
                            <div className={classNames(layout.horizontal, layout.alignCenter)}>
                                <Avatar size={2} key={occupancy.uuid}>{occupancy.boxNumber}</Avatar>
                                <div className={layout.paddingSmall}>
                                    <PushButton onClick={handleOpenBox}>
                                        {t("OpenBox")}
                                    </PushButton>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </View>
    )
}


const styles = stylesheet({
    bigger: {
        fontSize: "1.25em",
    },
});